import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft, Edit, Trash2, Check, Package, UserCheck, UserX
} from 'lucide-react';
import { canDelete, canEdit } from '../../view/Permissions';
import { PlanService, attachSubscriberStats, Panel, PlanTypeBadge, ErrorBanner } from './PlanShared';

const SinglePlan = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const hasEditPermission = canEdit('plans');
    const hasDeletePermission = canDelete('plans');

    const loadPlan = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await PlanService.getById(id);
            setPlan(data ? attachSubscriberStats(data) : null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { loadPlan(); }, [loadPlan]);

    const getFinalPrice = () => {
        if (!plan) return 0;
        if (!plan.discountAmount) return plan.price;
        if (plan.discountType === 'percentage') return plan.price * (1 - plan.discountAmount / 100);
        return Math.max(0, plan.price - plan.discountAmount);
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this plan? This action cannot be undone.')) return;
        setDeleting(true);
        try {
            await PlanService.delete(id);
            navigate('/admin/plans');
        } catch (err) {
            window.alert(err.message);
            setDeleting(false);
        }
    };

    const goBack = () => navigate('/admin/plans');
    const goToEdit = () => navigate(`/admin/plans/${id}/edit`);
    const goToActiveUsers = () => navigate(`/admin/plans/${id}/active-users`);
    const goToInactiveUsers = () => navigate(`/admin/plans/${id}/inactive-users`);

    if (loading) {
        return (
            <div className="bg-gray-50 min-h-screen p-4 rounded-2xl">
                <div className="text-center py-24">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-400 border-t-transparent mx-auto" />
                    <p className="text-sm mt-4 text-gray-500">Loading plan...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-gray-50 min-h-screen p-4 rounded-2xl space-y-4">
                <ErrorBanner message={error} onRetry={loadPlan} />
                <button onClick={goBack} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl text-orange-500 hover:bg-orange-50">
                    <ArrowLeft size={16} /> Back to Plans
                </button>
            </div>
        );
    }

    if (!plan) {
        return (
            <div className="bg-gray-50 min-h-screen p-4 rounded-2xl">
                <div className="text-center py-24 rounded-2xl bg-white border border-orange-200">
                    <Package size={32} className="mx-auto mb-3 opacity-30 text-gray-400" />
                    <p className="text-sm text-gray-500">Plan not found</p>
                    <button onClick={goBack} className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl text-orange-500 hover:bg-orange-50">
                        <ArrowLeft size={16} /> Back to Plans
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4 sm:space-y-6 px-2 sm:px-0 bg-gray-50 min-h-screen p-4 rounded-2xl">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 rounded-2xl border border-orange-200 shadow-sm">
                <div className="flex items-center gap-3 min-w-0">
                    <button onClick={goBack} className="p-2 rounded-xl transition-colors text-gray-500 hover:bg-orange-50 hover:text-orange-500 shrink-0">
                        <ArrowLeft size={18} />
                    </button>
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${plan.status === 'active' ? 'from-orange-500 to-amber-600' : 'from-gray-400 to-gray-500'} flex items-center justify-center shrink-0`}>
                        <Package size={20} className="text-white" />
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <h1 className="text-lg sm:text-xl font-bold truncate text-gray-800">{plan.name}</h1>
                            <PlanTypeBadge planType={plan.planType} />
                        </div>
                        <p className="text-xs text-gray-500">Created {new Date(plan.createdAt).toLocaleDateString()}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {hasEditPermission && (
                        <button onClick={goToEdit} className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-all shadow-sm bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:shadow-orange-500/30">
                            <Edit size={16} /> Edit
                        </button>
                    )}
                    {hasDeletePermission && (
                        <button disabled={deleting} onClick={handleDelete} className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-colors text-red-500 border border-red-200 hover:bg-red-50 disabled:opacity-50">
                            <Trash2 size={16} /> {deleting ? 'Deleting...' : 'Delete'}
                        </button>
                    )}
                </div>
            </div>

            {/* Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Panel>
                    <p className="text-xs font-medium text-gray-500">Status</p>
                    <div className="flex items-center gap-2 mt-1">
                        <span className={`w-2 h-2 rounded-full ${plan.status === 'active' ? 'bg-orange-500 animate-pulse' : 'bg-gray-400'}`} />
                        <span className="text-sm font-semibold capitalize text-orange-500">{plan.status}</span>
                    </div>
                </Panel>
                <Panel>
                    <p className="text-xs font-medium text-gray-500">Pricing</p>
                    <div className="flex items-baseline gap-2 mt-1 flex-wrap">
                        <span className="text-2xl font-bold text-orange-500">₹{getFinalPrice().toFixed(2)}</span>
                        {plan.isDiscountApplicable && plan.discountAmount > 0 && <span className="text-sm line-through text-gray-400">₹{plan.price}</span>}
                        <span className="text-xs text-gray-500">/ {plan.planType === 'yearly' ? 'year' : 'month'}</span>
                    </div>
                </Panel>
            </div>

            <Panel>
                <p className="text-xs font-medium mb-1 text-gray-500">Description</p>
                <p className="text-sm text-gray-700">{plan.description}</p>
            </Panel>

            <Panel>
                <p className="text-xs font-medium mb-3 text-gray-500">Features</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {plan.features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-2 rounded-lg px-3 py-2 bg-orange-50">
                            <Check size={14} className="text-orange-500" />
                            <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                    ))}
                </div>
            </Panel>

            <Panel>
                <p className="text-xs font-medium mb-3 text-gray-500">Subscriber Statistics</p>
                <div className="grid grid-cols-3 gap-3">
                    <div className="text-center">
                        <p className="text-2xl font-bold text-orange-500">{plan.subscriberCount || 0}</p>
                        <p className="text-[10px] text-gray-500">Total</p>
                    </div>
                    <div className="text-center cursor-pointer rounded-lg p-2 transition-colors hover:bg-orange-100"
                        onClick={() => plan.activeUsers > 0 && goToActiveUsers()}>
                        <p className={`text-2xl font-bold ${plan.activeUsers > 0 ? 'text-orange-500' : 'text-gray-400'}`}>
                            {plan.activeUsers || 0}
                        </p>
                        <p className="text-[10px] flex items-center justify-center gap-1 text-gray-500">
                            <UserCheck size={12} /> Active
                        </p>
                    </div>
                    <div className="text-center cursor-pointer rounded-lg p-2 transition-colors hover:bg-gray-100"
                        onClick={() => plan.inactiveUsers > 0 && goToInactiveUsers()}>
                        <p className={`text-2xl font-bold ${plan.inactiveUsers > 0 ? 'text-gray-500' : 'text-gray-400'}`}>
                            {plan.inactiveUsers || 0}
                        </p>
                        <p className="text-[10px] flex items-center justify-center gap-1 text-gray-500">
                            <UserX size={12} /> Inactive
                        </p>
                    </div>
                </div>
            </Panel>

            <Panel className="grid grid-cols-2 gap-4 text-xs bg-gray-50">
                <div>
                    <span className="font-medium text-gray-500">Created</span>
                    <p className="text-gray-700">{new Date(plan.createdAt).toLocaleString()}</p>
                </div>
                <div>
                    <span className="font-medium text-gray-500">Last Updated</span>
                    <p className="text-gray-700">{new Date(plan.updatedAt).toLocaleString()}</p>
                </div>
            </Panel>
        </div>
    );
};

export default SinglePlan;